import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { mocApi } from '../api/client';
import { fmtDateTime } from '../lib/mocUtils';
import { Skeleton, EmptyBox } from './Validations';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Bell, ListChecks, ShieldWarning, CheckSquare, Info, Circle } from '@phosphor-icons/react';

const ICONS = { action: ListChecks, validation: CheckSquare, pssr: ShieldWarning, info: Info };
const TABS = [
  ['all', 'Toutes'], ['validation', 'Approbations'], ['action', 'Actions'], ['pssr', 'PSSR'], ['info', 'Autres'],
];

export default function Notifications() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState('all');
  const navigate = useNavigate();

  useEffect(() => { mocApi.listNotifications().then(setItems); }, []);

  const markRead = async (id) => {
    setItems((list) => list.map((n) => n.id === id ? { ...n, read: true } : n));
    await mocApi.markNotificationRead(id);
  };
  const markAll = async () => {
    const unreadItems = (items || []).filter((n) => !n.read);
    setItems((list) => list.map((n) => ({ ...n, read: true })));
    await Promise.all(unreadItems.map((n) => mocApi.markNotificationRead(n.id)));
  };

  const unread = (items || []).filter((n) => !n.read).length;
  const counts = useMemo(() => {
    const c = { all: 0, validation: 0, action: 0, pssr: 0, info: 0 };
    (items || []).filter((n) => !n.read).forEach((n) => { c.all++; c[n.type] = (c[n.type] || 0) + 1; });
    return c;
  }, [items]);

  // Filter by tab, then sort unread-first (recent first within each group).
  const visible = useMemo(() => {
    const list = (items || []).filter((n) => tab === 'all' || n.type === tab);
    return [...list].sort((a, b) => {
      if (!!a.read !== !!b.read) return a.read ? 1 : -1;
      return new Date(b.at || 0) - new Date(a.at || 0);
    });
  }, [items, tab]);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Centre de notifications</div>
          <h1 className="font-display text-3xl font-black tracking-tight mt-1">Notifications {unread > 0 && <span className="text-primary">({unread})</span>}</h1>
        </div>
        {unread > 0 && <Button size="sm" variant="outline" data-testid="mark-all-read" onClick={markAll}>Tout marquer lu</Button>}
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map(([key, label]) => (
          <button key={key} data-testid={`notif-tab-${key}`} onClick={() => setTab(key)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === key ? 'border-primary text-primary font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {label}
            {counts[key] > 0 && <span className="ml-1.5 text-[11px] font-bold text-white bg-sky-500 rounded-full px-1.5 py-0.5">{counts[key]}</span>}
          </button>
        ))}
      </div>

      {!items ? <Skeleton /> : visible.length === 0 ? <EmptyBox icon={Bell} text="Aucune notification." /> : (
        <div className="space-y-2">
          {visible.map((n) => {
            const Icon = ICONS[n.type] || Info;
            return (
              <Card key={n.id} data-testid={`notif-${n.id}`}
                className={`rounded-sm p-4 flex items-start gap-3 ${!n.read ? 'border-l-4 border-l-primary' : 'opacity-70'}`}>
                <Icon size={20} weight="bold" className="text-primary shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{n.title}</span>
                    {!n.read && <Circle size={8} weight="fill" className="text-primary" />}
                  </div>
                  <p className="text-sm text-muted-foreground">{n.body}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    {n.mocId && <button onClick={() => navigate(`/moc/${n.mocId}`)} className="text-xs font-semibold text-primary hover:underline">{n.mocId}</button>}
                    <span className="text-xs text-muted-foreground">{fmtDateTime(n.at)}</span>
                  </div>
                </div>
                {!n.read && <button data-testid={`mark-read-${n.id}`} onClick={() => markRead(n.id)} className="text-xs text-primary hover:underline shrink-0">Marquer lu</button>}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
